import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder, ResultDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import isEmpty from 'lodash/isEmpty';
import { PaginationProps, QueryResponse } from 'types/paginationAndFilter';
import { objectToQueryParams } from 'utils/linkUtil';
import { QueryParams } from '../common/types';

type Options<DataType, TagTypes extends string> = {
    providesTags?:
        | ResultDescription<
              TagTypes,
              QueryResponse<DataType>,
              QueryParams,
              FetchBaseQueryError,
              FetchBaseQueryMeta | undefined
          >
        | undefined;
    secondarySort?: string[];
    keepUnusedDataFor?: number;
    dedupeKey?: (item: DataType) => string | number;
} & PaginationProps;

const serializeArgsWithoutPage = (endpointName: string, { page: _page, ...rest }: PaginationProps) =>
    `${endpointName}(${JSON.stringify(rest)})`;

const dedupeContent = <DataType>(content: DataType[], dedupeKey: (item: DataType) => string | number) => {
    const seen = new Set<string | number>();
    return content.filter((item) => {
        const key = dedupeKey(item);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
};

export const accumulatedQueryWithFilters = <DataType>(
    builder: EndpointBuilder<
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
        string,
        'portalAPI'
    >,
    endpoint: ((arg: string | number) => string) | string,
    options: Options<DataType, string> = {}
) =>
    builder.query<QueryResponse<DataType>, PaginationProps>({
        queryFn: async (queryArg, api, _extraOptions, baseQuery) => {
            const {
                filters = options.filters ?? '',
                passiveFilters = options.passiveFilters ?? '',
                sort = options.sort ?? '',
                page = options.page ?? 0,
                pageSize = options.pageSize ?? 10,
                id = options.id ?? '',
                extraParams: callExtraParams,
            } = queryArg;
            // A call's own params (useWithDrafts') add to the endpoint's instead of replacing them.
            const extraParams = { ...options.extraParams, ...callExtraParams };
            const finalEndpoint = typeof endpoint === 'string' ? endpoint : endpoint(id ?? '');
            const secondary = options.secondarySort;
            const effectiveSort = secondary?.length
                ? (() => {
                      const resolved = sort || options.sort || '';
                      const primaries = Array.isArray(resolved) ? resolved : resolved ? [resolved] : [];
                      return primaries.length ? [...primaries, ...secondary] : secondary;
                  })()
                : sort;
            const buildUrl = (requestPage: number) =>
                `${finalEndpoint}${objectToQueryParams({
                    filter: passiveFilters
                        ? [passiveFilters, filters].filter((item) => !isEmpty(item)).join(';')
                        : filters,
                    sort: effectiveSort,
                    page: requestPage,
                    size: pageSize,
                    ...extraParams,
                })}`;

            const fetchPage = async (requestPage: number) => {
                const result = await baseQuery(buildUrl(requestPage));
                return result.error ? { error: result.error } : { data: result.data as QueryResponse<DataType> };
            };

            // Tag invalidation re-runs this endpoint with the args of the LAST fetched page only.
            // Detect that case (the cache's last merged page is already the requested one) and
            // refresh every accumulated page instead, otherwise the stale page-N response would
            // just be re-appended while pages 0..N-1 keep showing outdated rows. A load-more for
            // page N is distinguishable because the cache then still ends at page N-1.
            const state = api.getState() as {
                portalAPI?: { queries?: Record<string, { data?: { number?: number } } | undefined> };
            };
            const cacheKey = serializeArgsWithoutPage(api.endpoint, queryArg);
            const lastMergedPage = state.portalAPI?.queries?.[cacheKey]?.data?.number;
            const isRefreshOfAccumulated = page > 0 && lastMergedPage === page;

            if (!isRefreshOfAccumulated) {
                return fetchPage(page);
            }

            const results = await Promise.all(
                Array.from({ length: page + 1 }, (_item, requestPage) => fetchPage(requestPage))
            );
            const failed = results.find((result) => result.error);
            if (failed?.error) {
                return { error: failed.error };
            }
            const pages = results.map((result) => result.data as QueryResponse<DataType>);
            const combined = pages.flatMap((pageData) => pageData.content);
            return {
                data: {
                    ...pages[pages.length - 1],
                    content: options.dedupeKey ? dedupeContent(combined, options.dedupeKey) : combined,
                    // Marks the combined window as a full replacement so merge discards the stale accumulation.
                    first: true,
                },
            };
        },
        merge: (currentCache, newItems) => {
            if (newItems.first) {
                return newItems;
            }
            const { dedupeKey } = options;
            if (!dedupeKey) {
                return { ...newItems, content: [...currentCache.content, ...newItems.content] };
            }
            // Offset pagination can re-serve an item already accumulated from an earlier page
            // (e.g. rows shifting between requests), which would render it twice.
            const seen = new Set(currentCache.content.map(dedupeKey));
            const freshItems = newItems.content.filter((item) => !seen.has(dedupeKey(item)));
            return { ...newItems, content: [...currentCache.content, ...freshItems] };
        },
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
            // Page is excluded from query key.
            // If other args are equal and page changes, we accumulate the data in query cache with merge function.
            return serializeArgsWithoutPage(endpointName, queryArgs);
        },
        forceRefetch: ({ currentArg, previousArg }) => {
            return currentArg !== previousArg;
        },
        providesTags: options.providesTags,
        keepUnusedDataFor: options.keepUnusedDataFor,
    });

export const paginatedQueryWithFilters = <DataType>(
    builder: EndpointBuilder<
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
        string,
        'portalAPI'
    >,
    endpoint: string,
    options: Options<DataType, string> = {}
) =>
    builder.query<QueryResponse<DataType>, PaginationProps>({
        query: ({
            filters = options.filters ?? '',
            sort = options.sort ?? '',
            page = options.page ?? 0,
            pageSize = options.pageSize ?? 10,
        }) =>
            `${endpoint}${objectToQueryParams({
                filter: filters,
                sort,
                page,
                size: pageSize,
            })}`,
        providesTags: options.providesTags,
    });
